import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #prefetchAbortController = null;
  /** @type {AbortController | null} */
  #modalFetchAbortController = null;
  #chooseOpenInProgress = false;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const resourceCard = /** @type {HTMLElement | null} */ (this.closest('.resource-card[data-resource-type="product"]'));
    const resourceCardLinkEl = resourceCard?.querySelector(':scope > .resource-card__link');
    const linkFromResourceCard =
      resourceCardLinkEl instanceof HTMLAnchorElement ? resourceCardLinkEl : null;
    const productLink =
      productCard?.getProductCardLink() ??
      hotspotProduct?.getHotspotProductLink() ??
      linkFromResourceCard;

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#prefetchAbortController?.abort();
    this.#modalFetchAbortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click (Choose).
   * preventDefault / stopPropagation / stopImmediatePropagation: after Quick Add handles Choose, stop bubbling to
   * ancestors (card, form, etc.) and stop other listeners on this button in the same phase. Pair with Stylux capture
   * early return for `quick-add__button--choose` (document capture runs first).
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const quickAddAllowed = document.body.dataset.themeQuickAddEnabled;
    if (quickAddAllowed === 'false') {
      const fallbackUrl = this.productPageUrl;
      if (fallbackUrl) window.location.assign(fallbackUrl);
      return;
    }

    const qaDialogRoot = document.getElementById('quick-add-dialog');
    if (!(qaDialogRoot instanceof QuickAddDialog)) {
      const fallbackUrl = this.productPageUrl;
      if (fallbackUrl) window.location.assign(fallbackUrl);
      return;
    }

    if (this.#chooseOpenInProgress) {
      return;
    }
    this.#chooseOpenInProgress = true;

    try {
      const currentUrl = this.productPageUrl;

      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        let html = null;
        try {
          html = await this.fetchProductPage(currentUrl, { forModalOpen: true });
        } catch {
          html = null;
        }
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      }

      /* Rebuy Reporting — COPY TO #quick-add-dialog for modal submits (see product-form). Remove with snippets + block toggle. */
      const qaHost = document.getElementById('quick-add-dialog');
      if (qaHost instanceof HTMLElement) {
        const attribution =
          typeof this.dataset.rebuyCartAttribution === 'string' ? this.dataset.rebuyCartAttribution.trim() : '';
        if (attribution) {
          qaHost.dataset.rebuyCartAttribution = attribution;
          const widgetId =
            typeof this.dataset.rebuyWidgetId === 'string' ? this.dataset.rebuyWidgetId.trim() : '';
          if (widgetId) qaHost.dataset.rebuyWidgetId = widgetId;
          else delete qaHost.dataset.rebuyWidgetId;
        } else {
          delete qaHost.dataset.rebuyCartAttribution;
          delete qaHost.dataset.rebuyWidgetId;
        }
      }

      this.#openQuickAddModal();
    } finally {
      this.#chooseOpenInProgress = false;
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {{ forModalOpen?: boolean }} [options] - `forModalOpen: true` uses a separate AbortController from hover prefetch; modal opens do not abort prefetch (avoids unhandled AbortError races).
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl, options = {}) {
    const forModalOpen = options.forModalOpen === true;
    /** @type {AbortController | undefined} */
    let ac;

    try {
      if (!productPageUrl) return null;

      if (forModalOpen) {
        this.#modalFetchAbortController?.abort();
      } else {
        this.#prefetchAbortController?.abort();
      }

      ac = new AbortController();
      if (forModalOpen) {
        this.#modalFetchAbortController = ac;
      } else {
        this.#prefetchAbortController = ac;
      }

      const response = await fetch(productPageUrl, { signal: ac.signal }).catch(() => null);

      if (!response?.ok) {
        return null;
      }

      const responseText = await response.text().catch(() => null);
      if (responseText == null) {
        return null;
      }

      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch {
      return null;
    } finally {
      if (ac) {
        if (forModalOpen) {
          if (this.#modalFetchAbortController === ac) {
            this.#modalFetchAbortController = null;
          }
        } else if (this.#prefetchAbortController === ac) {
          this.#prefetchAbortController = null;
        }
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.dataset.resourceCardQuickAdd === 'true') return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.kickstarter === 'true') return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) {
      viewMoreDetailsLink.href = anchorElement.href;
      viewMoreDetailsLink.textContent = anchorElement.textContent;
    }
    if (mobileProductTitle) {
      mobileProductTitle.href = anchorElement.href;
      mobileProductTitle.textContent = anchorElement.textContent;
    }
  };

  #handleDialogClose = () => {
    const outer = document.getElementById('quick-add-dialog');
    if (outer instanceof HTMLElement) {
      delete outer.dataset.rebuyCartAttribution; /* Rebuy Reporting teardown — product-form modal path */
      delete outer.dataset.rebuyWidgetId;
    }

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
